import { getService } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { type Recyclable } from './types';

/**
 * Registers modules that recycle when the database content ID changes.
 * Calls setup(initialContentId) on all modules, then subscribes to
 * databaseContentIdChanged. On each event, disposes all modules and
 * calls setup(newContentId).
 *
 * @param modules - Recyclable modules to manage. Execution order is array order.
 * @param initialContentId - Content ID for the initial setup.
 * @returns A cleanup function that disposes all modules and unsubscribes from the event bus.
 */
export function registerRecyclableModules(
  modules: Recyclable[],
  initialContentId: string
): VoidFunction {
  function setupAll(contentId: string) {
    for (const module of modules) {
      module.setup(contentId);
    }
  }

  function disposeAll() {
    for (const module of modules) {
      try {
        module.dispose();
      } catch (error) {
        captureException(error);
      }
    }
  }

  setupAll(initialContentId);

  const eventBus = getService('EventBus');

  const onContentIdChanged = (data: { contentId: string }) => {
    disposeAll();
    setupAll(data.contentId);
  };

  eventBus.on('databaseContentIdChanged', onContentIdChanged);

  return () => {
    disposeAll();
    eventBus.remove('databaseContentIdChanged', onContentIdChanged);
  };
}
